import { useMutation } from '@tanstack/vue-query';

/**
 * Wave 1 — property access modals.
 *
 * Thin wrappers around POST /api/auth/phone/send and
 * POST /api/auth/phone/verify. The server side reads the phone from
 * the authenticated user, so the client doesn't need to pass it.
 *
 * Bearer token is attached automatically by the global $fetch wrapper
 * for authenticated routes — but we also forward it explicitly to
 * stay consistent with the rest of /api/queries and /api/mutations.
 */

type SendResponse = {
  success: true;
  message: string;
};

type VerifyResponse = {
  success: true;
  data: {
    phoneVerified: true;
    // server returns the updated prisma user — kept loose here
    // because the client only needs the verification flag.
    user: unknown;
  };
};

export const useSendPhoneOtp = () => {
  const authStore = useAuthStore();

  return useMutation({
    mutationFn: () =>
      $fetch<SendResponse>('/api/auth/phone/send', {
        method: 'POST',
        headers: authStore.token ? { Authorization: `Bearer ${authStore.token}` } : undefined,
      }),
  });
};

export const useVerifyPhoneOtp = () => {
  const authStore = useAuthStore();

  return useMutation({
    mutationFn: (code: string) =>
      $fetch<VerifyResponse>('/api/auth/phone/verify', {
        method: 'POST',
        body: { code },
        headers: authStore.token ? { Authorization: `Bearer ${authStore.token}` } : undefined,
      }),
    onSuccess: () => {
      // Reflect verification immediately in the auth store so
      // useAccessLevel switches from 'registered' to 'verified'
      // without waiting for a refetch.
      if (authStore.user) {
        authStore.updateUser({ ...authStore.user, phoneVerified: true });
      }
    },
  });
};
